import {
  Building2,
  ExternalLink,
  Factory,
  FileText,
  Globe,
  Package,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { Badge } from "@b2b/ui-components";
import { buildBusinessProfileSections } from "../utils/businessProfileDisplay";

interface CompanyOverviewProps {
  company?: Record<string, unknown>;
  businessInfo?: Record<string, unknown>;
  companyDescription?: string;
  certifications?: unknown[];
  resolveIndustryLabel?: (value: string) => string;
  seller?: Record<string, unknown>;
}

const SECTION_ICONS: Record<string, typeof Building2> = {
  company: Building2,
  "factory-warehouse": Factory,
  trade: Truck,
  quality: ShieldCheck,
  online: Globe,
};

export const CompanyOverview = ({
  company,
  businessInfo,
  companyDescription,
  certifications,
  resolveIndustryLabel,
  seller,
}: CompanyOverviewProps) => {
  const { description, highlights, sections } = buildBusinessProfileSections({
    company,
    businessInfo,
    companyDescription,
    resolveIndustryLabel,
    seller,
  });

  const certBadges = (() => {
    const list: string[] = [];
    const qualityFromInfo = businessInfo?.qualityCertifications;
    if (Array.isArray(qualityFromInfo)) {
      qualityFromInfo.forEach((cert) => {
        if (typeof cert === "string" && cert.trim()) list.push(cert.trim());
      });
    }
    certifications?.forEach((cert) => {
      const name =
        typeof cert === "object" && cert !== null
          ? String((cert as { name?: unknown }).name || "").trim()
          : String(cert || "").trim();
      if (name) list.push(name);
    });
    return Array.from(new Set(list));
  })();

  const hasContent =
    description ||
    highlights.length > 0 ||
    sections.length > 0 ||
    certBadges.length > 0;

  if (!hasContent) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-10 text-center">
        <Package className="mx-auto mb-3 h-8 w-8 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">No business profile details yet</p>
        <p className="mt-1 text-xs text-gray-500">
          Company information will appear here once the seller completes their business profile.
        </p>
      </div>
    );
  }

  const renderDescription = () => {
    if (!description) return null;
    const looksLikeHtml = /<[^>]+>/.test(description);
    if (looksLikeHtml || description.includes("**") || description.includes("*")) {
      return (
        <div
          className="prose prose-sm max-w-none text-gray-700"
          dangerouslySetInnerHTML={{
            __html: description
              .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
              .replace(/\*(.*?)\*/g, "<em>$1</em>")
              .replace(/\n/g, "<br />"),
          }}
        />
      );
    }
    return (
      <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{description}</p>
    );
  };

  return (
    <div className="space-y-6">
      {description ? (
        <section className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6">
          <h3 className="mb-3 text-base font-semibold text-[#1A2A3D]">About the Company</h3>
          {renderDescription()}
        </section>
      ) : null}

      {highlights.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {highlights.map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-white p-3 sm:p-4"
            >
              <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                {item.label}
              </dt>
              <dd className="mt-1 text-sm font-semibold leading-snug text-[#1A2A3D]">
                {item.value}
              </dd>
            </div>
          ))}
        </div>
      ) : null}

      {certBadges.length > 0 ? (
        <section className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6">
          <h3 className="mb-3 text-base font-semibold text-[#1A2A3D]">Certifications</h3>
          <div className="flex flex-wrap gap-2">
            {certBadges.map((cert) => (
              <Badge
                key={cert}
                className="border-green-200 bg-green-50 text-green-800 hover:bg-green-50"
              >
                {cert}
              </Badge>
            ))}
          </div>
        </section>
      ) : null}

      {sections.map((section) => {
        const Icon = SECTION_ICONS[section.id] || Building2;
        return (
          <section
            key={section.id}
            className="overflow-hidden rounded-xl border border-gray-100 bg-white"
          >
            <div className="flex items-center gap-2 border-b border-gray-100 bg-slate-50/80 px-5 py-3 sm:px-6">
              <Icon className="h-4 w-4 text-[#FF6A00]" />
              <h3 className="text-sm font-semibold text-[#1A2A3D] sm:text-base">
                {section.title}
              </h3>
            </div>
            <dl className="grid gap-x-8 gap-y-4 p-5 sm:grid-cols-2 sm:p-6">
              {section.fields.map((field) => {
                const labelLower = field.label.toLowerCase();
                const isLink =
                  labelLower.includes("website") ||
                  labelLower.includes("catalog") ||
                  labelLower.includes("facebook") ||
                  labelLower.includes("linkedin") ||
                  labelLower.includes("twitter") ||
                  labelLower.includes("youtube") ||
                  labelLower.includes("instagram") ||
                  labelLower.includes("video");
                const href = field.value.startsWith("http")
                  ? field.value
                  : isLink
                    ? `https://${field.value}`
                    : null;
                const showIconLink = field.linkDisplay === "icon" && href;
                const isPdfCatalog =
                  labelLower.includes("catalog") ||
                  /\.pdf($|\?)/i.test(field.value);
                const LinkIcon = isPdfCatalog ? FileText : ExternalLink;

                return (
                  <div key={`${section.id}-${field.label}`} className="min-w-0">
                    <dt className="text-xs font-medium text-gray-500">{field.label}</dt>
                    <dd className="mt-1 text-sm font-medium text-gray-900 break-words">
                      {showIconLink ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#FF6A00] transition-colors hover:border-[#FF6A00]/40 hover:bg-orange-50"
                          aria-label={`Open ${field.label.toLowerCase()}`}
                          title={`Open ${field.label.toLowerCase()}`}
                        >
                          <LinkIcon className="h-4 w-4" aria-hidden />
                        </a>
                      ) : href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF6A00] hover:underline"
                        >
                          {field.value}
                        </a>
                      ) : (
                        field.value
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        );
      })}
    </div>
  );
};
